import { prExternalId, prUrlFromExternalId } from './agent-association.types.js';

/**
 * Parsing helpers for GitHub URLs pasted into the app. Shared by the web
 * composer (which detects a pasted PR link), the chat transcript (which turns
 * a PR link an agent printed into a badge), and the server (which resolves it
 * to a repo + head branch).
 */

/**
 * Source of the pull-request URL pattern, as a string so surfaces that can
 * only carry a serialized pattern — a plugin manifest's content scanner and
 * its context-URL matcher — declare the same shape this module parses instead
 * of hand-copying it. Three capture groups: owner, repo, number.
 *
 * The pattern matches mid-text (a link written into a sentence or printed by
 * `gh pr create` is the normal case), so `github.com` is guarded by a
 * lookbehind rather than an anchor: it has to start a host, not merely appear
 * somewhere. Without it, `notgithub.com/a/b/pull/1` and — worse, since a
 * pasted link becomes a reference chip — `evil.example/github.com/a/b/pull/1`
 * or `evil.example/?u=github.com/a/b/pull/1` would all read as that pull
 * request.
 *
 * The number is closed by a negative lookahead so only a real PR-URL boundary
 * ends it: a subpath (`/files`, `/commits`, `/changes`), a raw-view suffix
 * (`.diff`, `.patch`), a `?query`, a `#fragment` (`#discussion_r123` on a link
 * copied out of a review comment), or the end of the link. Without it,
 * `…/pull/12ab` would parse as PR 12.
 */
export const GITHUB_PR_URL_SOURCE =
  '(?<![\\w.@/:=?&#-])(?:https?://)?(?:www\\.)?github\\.com/([A-Za-z0-9_.-]+)/([A-Za-z0-9_.-]+)/pull/(\\d+)(?![\\w-])';

const GITHUB_PR_URL = new RegExp(GITHUB_PR_URL_SOURCE, 'i');
const GITHUB_PR_URL_GLOBAL = new RegExp(GITHUB_PR_URL_SOURCE, 'gi');

export interface GitHubPrRef {
  owner: string;
  repo: string;
  number: number;
}

/** Strip a trailing `.git` that can ride along on repo names in some links. */
function cleanRepo(repo: string): string {
  return repo.replace(/\.git$/i, '');
}

/**
 * Extract the first GitHub pull-request reference from a string, or `null` when
 * there is none. Accepts bare and `https://`-prefixed forms, and every artifact
 * a link picks up on its way through a browser or a review comment: a subpath
 * (`/files`, `/commits`, `/changes`), a `.diff` / `.patch` raw view, a query
 * string, and a `#discussion_r…` fragment all resolve to the same PR.
 */
export function parseGitHubPrUrl(text: string): GitHubPrRef | null {
  const match = text.match(GITHUB_PR_URL);
  if (!match) return null;
  return { owner: match[1], repo: cleanRepo(match[2]), number: Number(match[3]) };
}

/** A pull request named somewhere in a block of text, in canonical form. */
export interface GitHubPrLink extends GitHubPrRef {
  /** `owner/repo#42` — the canonical id associations and context refs use. */
  externalId: string;
  /** Canonical `https://github.com/owner/repo/pull/42`, however it was written. */
  url: string;
}

/**
 * Every distinct pull request named in `text`, in the order they appear.
 *
 * Used where a PR link is buried in output nobody parsed — `gh pr create`
 * printing the URL it just opened, an agent quoting a link — so the surface
 * can present the PR itself rather than a bare URL. Deduped by canonical id,
 * so the same PR written twice (or in two spellings) yields one entry.
 */
export function parseGitHubPrUrls(text: string): GitHubPrLink[] {
  const links: GitHubPrLink[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(GITHUB_PR_URL_GLOBAL)) {
    const owner = match[1];
    const repo = cleanRepo(match[2]);
    const number = Number(match[3]);
    const externalId = prExternalId(`${owner}/${repo}`, number);
    if (seen.has(externalId)) continue;
    seen.add(externalId);
    // Non-null: the id was just built from a parsed owner/repo/number.
    links.push({ owner, repo, number, externalId, url: prUrlFromExternalId(externalId)! });
  }
  return links;
}
